import { HttpClientConfig, type HttpClientInterface } from 'core/infra/http/HttpClient'
import { PBIReport } from 'managed-analytics/domain/PBIReport'

import {
  mapApiToPBIDemoReports,
  mapApiToPBIDemoReport,
  mapApiToPBIExportableReports,
  mapApiToPBIReports,
  mapApiToPBIReport,
  mapApiToPBIToken,
  mapPBIReportToApi,
  RawPBIDemoReport,
  RawPBIExportableReport,
  RawPBIReport,
  RawPBIToken,
  RawPBIDemoReportWithToken,
  RawPBIReportWithoutProfile,
} from './PBIMapper'

export type GetTokenParams = Omit<RawPBIReport, RawPBIReport['report_name']>

export type PBIRepositoryDependencies = {
  httpClient: HttpClientInterface
}

export class PBIRepository {
  private httpClient: HttpClientInterface

  constructor({ httpClient }: PBIRepositoryDependencies) {
    this.httpClient = httpClient
  }

  async getReports(config?: HttpClientConfig) {
    const endpoint = '/pbi_reports'

    const { data } = await this.httpClient.get<Array<RawPBIReport>>(endpoint, config)

    try {
      return mapApiToPBIReports(data)
    } catch (error) {
      throw new Error('Failed to map API to PBI reports')
    }
  }

  async getReport(reportId: string, config?: HttpClientConfig) {
    const endpoint = `/pbi_reports/${reportId}`

    const { data } = await this.httpClient.get<RawPBIReportWithoutProfile>(endpoint, config)

    try {
      return mapApiToPBIReport(data)
    } catch (error) {
      throw new Error('Failed to map API to PBI report')
    }
  }

  async getDemoReports(config?: HttpClientConfig) {
    const endpoint = '/pbi_demo_reports'
    const { data } = await this.httpClient.get<Array<RawPBIDemoReport>>(endpoint, config)

    try {
      return mapApiToPBIDemoReports(data)
    } catch (error) {
      throw new Error('Failed to map API to PBI demo reports')
    }
  }

  async getDemoReport(reportId: string, config?: HttpClientConfig) {
    const endpoint = `/pbi_demo_reports/${reportId}`

    const { data } = await this.httpClient.get<RawPBIDemoReportWithToken>(endpoint, config)

    try {
      return mapApiToPBIDemoReport(data)
    } catch (error) {
      throw new Error('Failed to map API to PBI demo report')
    }
  }

  async getExportableReports(config?: HttpClientConfig) {
    const endpoint = '/pbi_exportable_reports'
    const { data } = await this.httpClient.get<Array<RawPBIExportableReport>>(endpoint, config)
    try {
      return mapApiToPBIExportableReports(data)
    } catch (error) {
      throw new Error('Failed to map API to exportable PBI reports')
    }
  }

  async getPbiToken(report: PBIReport, config?: HttpClientConfig) {
    const endpoint = '/pbi_token'
    const { data } = await this.httpClient.post<GetTokenParams, RawPBIToken>(
      endpoint,
      mapPBIReportToApi(report),
      config
    )

    try {
      return mapApiToPBIToken(data)
    } catch (error) {
      throw new Error('Failed to map API to PBI token')
    }
  }
}
